import { Link } from "react-router-dom";
import { Pill, MapPin, LineChart, ShieldCheck, ArrowRight, HeartHandshake, Bell, Users, MessagesSquare, Clock, Lock, LayoutDashboard, BookOpen } from "lucide-react";
import { DisclaimerBanner } from "../components/DisclaimerBanner";
import { PublicShell } from "../components/PublicShell";
import { ContactBlock } from "../components/ContactBlock";
import { useAuth } from "../context/AuthContext";
import { ARTICLES } from "../data/articles";

const HERO = "https://images.pexels.com/photos/15296464/pexels-photo-15296464.jpeg";

const openCrisis = () => window.dispatchEvent(new Event("open-crisis"));

const featuresOut = [
  { icon: Pill, title: "Taper Education", desc: "Ashton Manual reference dosing, equivalents and a printable letter for your GP.", to: "/taper", tag: "Free tool" },
  { icon: MapPin, title: "Find Support", desc: "Search recovery centres across the UK & Ireland by postcode and service type.", to: "/find-support", tag: "Free" },
  { icon: LineChart, title: "Progress Tracker", desc: "Log dose, mood and sleep. See gentle progress — never a shame counter.", to: "/auth", tag: "Free account" },
  { icon: HeartHandshake, title: "Crisis & Safety", desc: "One-tap access to Samaritans, NHS 111 and emergency help, any time.", action: "crisis", tag: "Always free" },
];

const featuresIn = [
  { icon: Pill, title: "Taper Tool", desc: "Reference dosing, diazepam equivalents and your GP letter.", to: "/app/taper" },
  { icon: MapPin, title: "Find Support", desc: "Recovery centres near you across the UK & Ireland.", to: "/app/locator" },
  { icon: LineChart, title: "Progress", desc: "Log your day, mood and sleep — and see gentle trends.", to: "/app/tracker" },
  { icon: Bell, title: "Reminders", desc: "Gentle dose, taper-step and journal nudges.", to: "/app/reminders" },
  { icon: Users, title: "Buddy", desc: "Share progress with someone you trust.", to: "/app/buddy" },
  { icon: MessagesSquare, title: "Community", desc: "Moderated, anonymous-friendly peer support.", to: "/app/community" },
  { icon: BookOpen, title: "Learn", desc: "Guides, in-depth articles & FAQs.", to: "/app/learn" },
  { icon: HeartHandshake, title: "Crisis & Safety", desc: "One-tap access to Samaritans, NHS 111 and emergency help, any time.", action: "crisis" },
];

const accountFeatures = [
  { icon: LineChart, title: "Progress tracking", desc: "Daily dose, mood & sleep logs with gentle trends." },
  { icon: Bell, title: "Reminders & journaling", desc: "Kind nudges for doses and steps — even when the app is closed." },
  { icon: MessagesSquare, title: "Peer community", desc: "Post and reply on moderated, anonymous-friendly boards." },
  { icon: Users, title: "Buddy support", desc: "Share milestones with someone you trust." },
];

export default function Landing() {
  const { user } = useAuth();

  return (
    <PublicShell>
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 py-12 lg:grid-cols-2 lg:py-20">
          <div className="animate-fade-up">
            <span className="mb-5 inline-block rounded-full bg-[#8FB8DE]/25 px-4 py-1.5 text-sm font-medium text-[#2C4763]">Harm-reduction companion · UK & Ireland</span>
            <h1 className="font-head text-4xl font-extrabold leading-tight tracking-tight text-[#2C363F] sm:text-5xl lg:text-6xl">Taper safely, one gentle step at a time.</h1>
            <p className="mt-5 max-w-lg text-lg leading-relaxed text-[#64748B]">TaperPath helps you reduce benzodiazepines under medical guidance, track how you feel, and find real recovery support near you. Warm, private, no judgement.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {user ? (
                <>
                  <Link to="/app" data-testid="go-dashboard-btn" className="flex items-center gap-2 rounded-full bg-[#4A7c59] px-6 py-3.5 font-semibold text-white shadow-lg shadow-[#4A7c59]/20 transition-transform duration-300 hover:-translate-y-1"><LayoutDashboard className="h-4 w-4" /> Go to your dashboard <ArrowRight className="h-4 w-4" /></Link>
                  <Link to="/articles" className="rounded-full border border-[#4A7c59]/30 bg-white px-6 py-3.5 font-semibold text-[#4A7c59] transition-transform duration-300 hover:-translate-y-1">Read the articles</Link>
                </>
              ) : (
                <>
                  <Link to="/auth" data-testid="get-started-btn" className="flex items-center gap-2 rounded-full bg-[#4A7c59] px-6 py-3.5 font-semibold text-white shadow-lg shadow-[#4A7c59]/20 transition-transform duration-300 hover:-translate-y-1">Get started <ArrowRight className="h-4 w-4" /></Link>
                  <Link to="/auth" data-testid="continue-anon-btn" className="rounded-full border border-[#4A7c59]/30 bg-white px-6 py-3.5 font-semibold text-[#4A7c59] transition-transform duration-300 hover:-translate-y-1">Continue anonymously</Link>
                </>
              )}
            </div>
          </div>
          <div className="relative animate-fade-up">
            <div className="overflow-hidden rounded-[2rem] shadow-2xl shadow-[#4A7c59]/10">
              <img src={HERO} alt="Sunlit forest path" className="h-[420px] w-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-4 flex items-center gap-3 rounded-2xl border border-border bg-white/90 p-4 shadow-lg backdrop-blur">
              <ShieldCheck className="h-8 w-8 text-[#4A7c59]" />
              <div><div className="font-head font-bold text-[#2C363F]">Private by default</div><div className="text-sm text-muted-foreground">Your data, your control.</div></div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-12">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {(user ? featuresIn : featuresOut).map((f, i) => {
            const inner = (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#4A7c59]/10 text-[#4A7c59]"><f.icon strokeWidth={1.8} className="h-6 w-6" /></div>
                  {f.tag && <span className="rounded-full bg-[#8FB8DE]/20 px-2.5 py-0.5 text-xs font-medium text-[#2C4763]">{f.tag}</span>}
                </div>
                <h3 className="font-head text-lg font-semibold text-[#2C363F]">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#4A7c59]">Open <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></span>
              </>
            );
            const cls = "group rounded-2xl border border-border bg-white p-6 shadow-sm transition-transform duration-300 hover:-translate-y-1";
            if (f.action === "crisis")
              return <button key={f.title} onClick={openCrisis} data-testid="landing-feature-crisis-safety" className={`${cls} text-left`} style={{ animationDelay: `${i * 80}ms` }}>{inner}</button>;
            return <Link key={f.title} to={f.to} data-testid={`landing-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={cls} style={{ animationDelay: `${i * 80}ms` }}>{inner}</Link>;
          })}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-8">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="font-head text-3xl font-bold text-[#2C363F]">Read &amp; learn</h2>
            <p className="mt-2 max-w-xl text-muted-foreground">Plain-English, judgement-free guides on benzodiazepines, tapering and recovery. Free to read — no account needed.</p>
          </div>
          <Link to="/articles" data-testid="landing-all-articles" className="hidden shrink-0 items-center gap-1 rounded-full border border-[#4A7c59]/30 bg-white px-5 py-2.5 text-sm font-semibold text-[#4A7c59] transition-transform duration-300 hover:-translate-y-0.5 sm:flex">All articles <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {ARTICLES.slice(0, 3).map((a) => (
            <Link key={a.slug} to={`/articles/${a.slug}`} data-testid={`landing-article-${a.slug}`}
              className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-white shadow-sm transition-transform duration-300 hover:-translate-y-1">
              <div className="h-44 overflow-hidden"><img src={a.image} alt={a.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
              <div className="flex flex-1 flex-col p-6">
                <div className="flex items-center gap-3 text-xs font-medium text-muted-foreground">
                  <span className="rounded-full bg-[#4A7c59]/10 px-2.5 py-0.5 text-[#4A7c59]">{a.category}</span>
                  <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {a.readTime}</span>
                </div>
                <h3 className="mt-3 font-head text-lg font-semibold leading-snug text-[#2C363F]">{a.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{a.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#4A7c59]">Read article <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></span>
              </div>
            </Link>
          ))}
        </div>
        <Link to="/articles" className="mt-6 flex items-center justify-center gap-1 text-sm font-semibold text-[#4A7c59] sm:hidden">All articles <ArrowRight className="h-4 w-4" /></Link>
      </section>

      {!user && (
        <section className="mx-auto max-w-7xl px-6 py-12">
          <div className="overflow-hidden rounded-[2rem] border border-[#4A7c59]/15 bg-[#4A7c59]/5 p-8 sm:p-12">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#4A7c59]"><Lock className="h-3.5 w-3.5" /> Free account</span>
              <h2 className="mt-4 font-head text-3xl font-bold text-[#2C363F]">Do more with a free account</h2>
              <p className="mt-2 text-muted-foreground">To save your progress, set reminders and post in the community, you'll need an account — it's free and takes seconds. Stay <strong>anonymous</strong>, or use <strong>email</strong> or <strong>Google</strong>. Your health data is private by default.</p>
            </div>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {accountFeatures.map((f) => (
                <div key={f.title} className="rounded-2xl border border-border bg-white p-5 shadow-sm">
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#8FB8DE]/20 text-[#2C4763]"><f.icon strokeWidth={1.8} className="h-5 w-5" /></div>
                  <h3 className="font-head text-base font-semibold text-[#2C363F]">{f.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{f.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/auth" data-testid="landing-create-account" className="flex items-center gap-2 rounded-full bg-[#4A7c59] px-6 py-3.5 font-semibold text-white shadow-lg shadow-[#4A7c59]/20 transition-transform duration-300 hover:-translate-y-1">Create free account <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/taper" className="rounded-full border border-[#4A7c59]/30 bg-white px-6 py-3.5 font-semibold text-[#4A7c59] transition-transform duration-300 hover:-translate-y-1">Try the Taper Tool first</Link>
            </div>
          </div>
        </section>
      )}

      {!user && (
        <section className="mx-auto max-w-7xl px-6 py-8">
          <ContactBlock />
        </section>
      )}

      <section className="mx-auto max-w-4xl px-6 pb-16"><DisclaimerBanner /></section>
    </PublicShell>
  );
}
